export type NivelGovbr = 'bronze' | 'prata' | 'ouro';

export interface ServicosServico {
  uid: string;
  titulo: string;
  descricao?: string;
  url: string;
  govbr: NivelGovbr | null;
  externo: boolean;
  palavras_chave?: string[];
}

export interface ServicosCategoria {
  uid: string;
  id: string;
  titulo: string;
  descricao?: string;
  exibir_descricao?: boolean;
  icone: string | null;
  link_extra: string | null;
  link_extra_rotulo: string | null;
  servicos: ServicosServico[];
}

export interface ServicosSugestao {
  id: string;
  tipo: 'categoria' | 'servico';
  titulo: string;
  href: string;
  externo: boolean;
  categoria?: string;
  termos?: string;
}

export function normalizar(texto: string): string {
  return Array.from(texto ?? '')
    .map((c) => c.normalize('NFD').replace(/[̀-ͯ]/g, '') || c)
    .join('')
    .toLowerCase();
}

export function descricaoVisivel(categoria: ServicosCategoria): boolean {
  return categoria.exibir_descricao === true && Boolean(categoria.descricao);
}

export function categoriaVisivel(categoria: ServicosCategoria): boolean {
  return (
    categoria.servicos.length > 0 ||
    descricaoVisivel(categoria) ||
    Boolean(categoria.link_extra)
  );
}

export function indiceDeBusca(
  categorias: ServicosCategoria[],
): ServicosSugestao[] {
  return categorias.filter(categoriaVisivel).flatMap((categoria) => [
    {
      id: categoria.uid,
      tipo: 'categoria' as const,
      titulo: categoria.titulo,
      href: `#${idDaCategoria(categoria.id)}`,
      externo: false,
    },
    ...categoria.servicos.map((servico) => ({
      id: servico.uid,
      tipo: 'servico' as const,
      titulo: servico.titulo,
      href: servico.url,
      externo: servico.externo,
      categoria: categoria.titulo,
      termos: normalizar(
        [servico.descricao, ...(servico.palavras_chave ?? [])]
          .filter(Boolean)
          .join(' '),
      ),
    })),
  ]);
}

export function filtrarSugestoes(
  indice: ServicosSugestao[],
  termo: string,
  { limite = 6, minimo = 2 }: { limite?: number; minimo?: number } = {},
): ServicosSugestao[] {
  const alvo = normalizar(termo.trim());
  if (alvo.length < minimo) return [];

  const relevancia = (sugestao: ServicosSugestao): number => {
    const posicao = normalizar(sugestao.titulo).indexOf(alvo);
    if (posicao === 0) return 0;
    if (posicao > 0) return 1;
    return sugestao.termos?.includes(alvo) ? 2 : -1;
  };

  return indice
    .map((sugestao, ordem) => ({
      sugestao,
      ordem,
      peso: relevancia(sugestao),
    }))
    .filter(({ peso }) => peso >= 0)
    .sort((a, b) => a.peso - b.peso || a.ordem - b.ordem)
    .slice(0, limite)
    .map(({ sugestao }) => sugestao);
}

export function trechoDestacado(
  titulo: string,
  termo: string,
): { antes: string; trecho: string; depois: string } {
  const alvo = normalizar(termo.trim());
  const posicao = alvo ? normalizar(titulo).indexOf(alvo) : -1;
  if (posicao < 0) return { antes: titulo, trecho: '', depois: '' };
  return {
    antes: titulo.slice(0, posicao),
    trecho: titulo.slice(posicao, posicao + alvo.length),
    depois: titulo.slice(posicao + alvo.length),
  };
}

export function idDaCategoria(id: string): string {
  return `servicos-${id}`;
}
